import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useMaintenancePolling } from './hooks/useMaintenancePolling';
import { AdminRoute } from './components/auth/AdminRoute';
import { ScrollToTop } from './components/layout/ScrollToTop';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ErrorBoundary } from './components/ErrorBoundary';
import { Layout } from './components/layout/Layout';
import { Toaster } from './components/ui/Toaster';
import { SseContextProvider } from './contexts/SseContext';
import { AdminCategoriesPage } from './pages/admin/AdminCategoriesPage';
import { AdminCategoryFormPage } from './pages/admin/AdminCategoryFormPage';
import { AdminDashboardPage } from './pages/admin/AdminDashboardPage';
import { AdminExternalApiKeysPage } from './pages/admin/AdminExternalApiKeysPage';
import { AdminPaymentGatewaysPage } from './pages/admin/AdminPaymentGatewaysPage';
import { AdminNumbersPage } from './pages/admin/AdminNumbersPage';
import { AdminProxiesPage } from './pages/admin/AdminProxiesPage';
import { AdminProductFormPage } from './pages/admin/AdminProductFormPage';
import { AdminProductsPage } from './pages/admin/AdminProductsPage';
import { AdminSystemConfigPage } from './pages/admin/AdminSystemConfigPage';
import { AdminTransactionsPage } from './pages/admin/AdminTransactionsPage';
import { AdminUserDetailsPage } from './pages/admin/AdminUserDetailsPage';
import { AdminUserMarginsPage } from './pages/admin/AdminUserMarginsPage';
import { AdminShamCashPaymentsPage } from './pages/admin/AdminShamCashPaymentsPage';
import { AdminSyriatelCashPaymentsPage } from './pages/admin/AdminSyriatelCashPaymentsPage';
import { AdminUsersPage } from './pages/admin/AdminUsersPage';
import { AboutPage } from './pages/AboutPage';
import { ApiKeysPage } from './pages/ApiKeysPage';
import { BalancePage } from './pages/BalancePage';
import { CatalogPage } from './pages/CatalogPage';
import { CategoryPage } from './pages/CategoryPage';
import DashboardPage from './pages/DashboardPage';
import { EmailVerificationPage } from './pages/EmailVerificationPage';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LoginPage } from './pages/LoginPage';
import { MaintenancePage } from './pages/MaintenancePage';
import { CallReceiverPage } from './pages/CallReceiverPage';
import { TelegramLoginRequestPage } from './pages/TelegramLoginRequestPage';
import { TelegramLoginVerifyPage } from './pages/TelegramLoginVerifyPage';
import { TelegramLoginAddCredentialsPage } from './pages/TelegramLoginAddCredentialsPage';
import { PermanentNumberDetailsPage } from './pages/PermanentNumberDetailsPage';
import { PermanentNumbersListPage } from './pages/PermanentNumbersListPage';
import { PermanentNumbersPage } from './pages/PermanentNumbersPage';
import { PermanentNumbersPurchasePage } from './pages/PermanentNumbersPurchasePage';
import { PrivacyPage } from './pages/PrivacyPage';
import { ProductPage } from './pages/ProductPage';
import { ProfilePage } from './pages/ProfilePage';
import { ProxiesListPage } from './pages/ProxiesListPage';
import { ProxiesPage } from './pages/ProxiesPage';
import { ProxiesPurchasePage } from './pages/ProxiesPurchasePage';
import { ProxyDetailsPage } from './pages/ProxyDetailsPage';
import { PurchasesPage } from './pages/PurchasesPage';
import { PurchaseTemporaryNumberPage } from './pages/PurchaseTemporaryNumberPage';
import { RechargePage } from './pages/RechargePage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SupportPage } from './pages/SupportPage';
import { TemporaryNumberDetailsPage } from './pages/TemporaryNumberDetailsPage';
import { TemporaryNumbersListPage } from './pages/TemporaryNumbersListPage';
import { TemporaryNumbersPage } from './pages/TemporaryNumbersPage';
import { TermsPage } from './pages/TermsPage';
import { TransactionsPage } from './pages/TransactionsPage';
import { isAuthenticated } from './utils/tokenManager';

type MaintenanceMode = 'none' | 'comprehensive' | 'bot_only' | 'website_only';

function App() {
  const { t } = useTranslation();
  const [maintenanceMode, setMaintenanceMode] = useState<MaintenanceMode>('none');
  const [maintenanceMessage, setMaintenanceMessage] = useState<string | null>(null);
  const [checkDone, setCheckDone] = useState(false);

  const auth = isAuthenticated();

  let isAdmin = false;
  if (auth) {
    try {
      const userData = JSON.parse(localStorage.getItem('user') || '{}');
      isAdmin = !!userData.isAdmin;
    } catch { /* ignore */ }
  }

  useMaintenancePolling({
    onMaintenanceChange: (mode, message) => {
      setMaintenanceMode(mode);
      setMaintenanceMessage(message);
      if (!checkDone) setCheckDone(true);
    },
    enabled: auth && !isAdmin,
  });

  const isWebsiteDown = maintenanceMode === 'comprehensive' || maintenanceMode === 'website_only';
  const showMaintenance = checkDone && auth && !isAdmin && isWebsiteDown;

  if (showMaintenance) {
    return <MaintenancePage message={maintenanceMessage} />;
  }

  return (
    <ErrorBoundary>
      <SseContextProvider>
        <div className="min-h-screen relative">
          <ScrollToTop />
          <Layout>
            <Routes>
              {/* Public Routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/" element={<Navigate to="/login" replace />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/support" element={<SupportPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/telegram-login-request" element={<TelegramLoginRequestPage />} />
              <Route path="/telegram-login-verify" element={<TelegramLoginVerifyPage />} />
              <Route path="/telegram-login-add-credentials" element={<TelegramLoginAddCredentialsPage />} />
              <Route path="/call/:sessionId" element={<CallReceiverPage />} />

              {/* Protected Routes */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <ProtectedRoute>
                    <ProfilePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/api-keys"
                element={
                  <ProtectedRoute>
                    <ApiKeysPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/balance"
                element={
                  <ProtectedRoute>
                    <BalancePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/recharge"
                element={
                  <ProtectedRoute>
                    <RechargePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/transactions"
                element={
                  <ProtectedRoute>
                    <TransactionsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalog"
                element={
                  <ProtectedRoute>
                    <CatalogPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalog/category/:categoryId"
                element={
                  <ProtectedRoute>
                    <CategoryPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/catalog/product/:productId"
                element={
                  <ProtectedRoute>
                    <ProductPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/purchases"
                element={
                  <ProtectedRoute>
                    <PurchasesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/temporary-numbers"
                element={
                  <ProtectedRoute>
                    <TemporaryNumbersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/temporary-numbers/purchase"
                element={
                  <ProtectedRoute>
                    <PurchaseTemporaryNumberPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/temporary-numbers/list"
                element={
                  <ProtectedRoute>
                    <TemporaryNumbersListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/temporary-numbers/:numberId"
                element={
                  <ProtectedRoute>
                    <TemporaryNumberDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/permanent-numbers"
                element={
                  <ProtectedRoute>
                    <PermanentNumbersPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/permanent-numbers/purchase"
                element={
                  <ProtectedRoute>
                    <PermanentNumbersPurchasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/permanent-numbers/list"
                element={
                  <ProtectedRoute>
                    <PermanentNumbersListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/permanent-numbers/:id"
                element={
                  <ProtectedRoute>
                    <PermanentNumberDetailsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proxies"
                element={
                  <ProtectedRoute>
                    <ProxiesPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proxies/purchase"
                element={
                  <ProtectedRoute>
                    <ProxiesPurchasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proxies/purchase/:serverId"
                element={
                  <ProtectedRoute>
                    <ProxiesPurchasePage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proxies/list"
                element={
                  <ProtectedRoute>
                    <ProxiesListPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/proxies/:proxyId"
                element={
                  <ProtectedRoute>
                    <ProxyDetailsPage />
                  </ProtectedRoute>
                }
              />

              {/* Admin Routes */}
              <Route
                path="/admin/dashboard"
                element={
                  <AdminRoute>
                    <AdminDashboardPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/categories"
                element={
                  <AdminRoute>
                    <AdminCategoriesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/categories/new"
                element={
                  <AdminRoute>
                    <AdminCategoryFormPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/categories/:categoryId/edit"
                element={
                  <AdminRoute>
                    <AdminCategoryFormPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/products"
                element={
                  <AdminRoute>
                    <AdminProductsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/products/new"
                element={
                  <AdminRoute>
                    <AdminProductFormPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/products/:productId/edit"
                element={
                  <AdminRoute>
                    <AdminProductFormPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users"
                element={
                  <AdminRoute>
                    <AdminUsersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/users/:userId"
                element={
                  <AdminRoute>
                    <AdminUserDetailsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/system-config"
                element={
                  <AdminRoute>
                    <AdminSystemConfigPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/external-api-keys"
                element={
                  <AdminRoute>
                    <AdminExternalApiKeysPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/payment-gateways"
                element={
                  <AdminRoute>
                    <AdminPaymentGatewaysPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/transactions"
                element={
                  <AdminRoute>
                    <AdminTransactionsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/numbers"
                element={
                  <AdminRoute>
                    <AdminNumbersPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/proxies"
                element={
                  <AdminRoute>
                    <AdminProxiesPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/user-margins"
                element={
                  <AdminRoute>
                    <AdminUserMarginsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/sham-cash-payments"
                element={
                  <AdminRoute>
                    <AdminShamCashPaymentsPage />
                  </AdminRoute>
                }
              />
              <Route
                path="/admin/syriatel-cash-payments"
                element={
                  <AdminRoute>
                    <AdminSyriatelCashPaymentsPage />
                  </AdminRoute>
                }
              />

              {/* 404 Route */}
              <Route
                path="*"
                element={
                  <div className="text-center">
                    <h1 className="text-4xl font-heading font-bold text-brand-accent mb-4">
                      {t('errors.404')}
                    </h1>
                  </div>
                }
              />
            </Routes>
          </Layout>
        </div>
        <Toaster />
      </SseContextProvider>
    </ErrorBoundary>
  );
}

export default App;
